<template>
    <v-form class="pb-4" data-cy="description-amenities-form">
        <v-row v-if="useIsAmenitiesLoading" dense>
            <v-sheet class="full-width d-flex justify-center py-2">
                <v-progress-linear color="primary" indeterminate class="mb-0" height="3px" />
            </v-sheet>
        </v-row>
        <v-row dense>
            <v-col cols="12" md="2">
                <span class="text-h6 text--secondary">
                    {{ $t('product_tabs.amenities') }}
                </span>
            </v-col>
            <v-col cols="12" md="10">
                <v-row dense>
                    <v-col v-for="(amenity, index) in amenities" :key="index" cols="12" sm="3" md="2">
                        <div class="text-subtitle-1 font-weight-medium">{{ $t(`amenities.${index}`) }}:</div>
                        <template v-if="amenity.length > 100">
                            <v-virtual-scroll :height="400" :items="amenity">
                                <template v-slot="{item}">
                                    <v-checkbox
                                        v-model="item.active"
                                        hide-details
                                        :label="item.name"
                                        density="compact"
                                        :data-cy="item.id"
                                        :readonly="isHotelDescriptionMode"
                                        @click="toggleAmenity(item)"
                                    />
                                </template>
                            </v-virtual-scroll>
                        </template>
                        <template v-else>
                            <div v-for="item in amenity" :key="item.id">
                                <v-checkbox
                                    v-model="item.active"
                                    hide-details
                                    :label="item.name"
                                    density="compact"
                                    :data-cy="item.id"
                                    :readonly="isHotelDescriptionMode"
                                    @click="toggleAmenity(item)"
                                />
                            </div>
                        </template>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
        <v-row no-gutters class="gc-4">
            <v-btn v-if="!isHotelDescriptionMode" data-cy="save" :disabled="isLoading" @click="save">
                {{ $t('save') }}
            </v-btn>
            <v-btn
                class="cancel-btn"
                data-cy="cancel"
                :disabled="isLoading"
                :to="{name: 'supplierProducts'}"
                variant="text"
            >
                {{ $t('cancel') }}
            </v-btn>
        </v-row>
    </v-form>
</template>

<script setup lang="ts">
    import {clone} from '@/utils/helpers'

    const isLoading = ref<boolean>(false)
    const selectedAmenities = ref([])
    const isHotelDescriptionMode: boolean = inject('isHotelDescriptionMode', false)
    const hotelDescriptionId: string = inject('hotelDescriptionId', null)

    const amenities = ref<any>({})

    // Eco Amenities list
    const ecoAmenities = ref([
        {id: 'linen-change', name: 'Linen Change', active: false, hoursOfUsage: 1, wattEnergy: 1},
        {id: 'towel-change', name: 'Towel Change', active: false, hoursOfUsage: 1, wattEnergy: 1},
        {id: 'mini-bar-fridge', name: 'Mini bar/fridge availability', active: false, hoursOfUsage: 1, wattEnergy: 1},
        {id: 'air-conditioning', name: 'Air conditioning usage', active: false, hoursOfUsage: 1, wattEnergy: 1},
        {id: 'hairdryer', name: 'Hairdryer', active: false, hoursOfUsage: 1, wattEnergy: 1},
        {
            id: 'tea-coffee',
            name: 'Tea & coffee making facilities (kettle)',
            active: false,
            hoursOfUsage: 1,
            wattEnergy: 1,
        },
    ])

    // Energy-Saving Solutions list
    const energySavingSolutions = ref([
        {id: 'heating-cooling', name: 'Heating/cooling system', active: false, hoursOfUsage: 1, wattEnergy: 1},
        {id: 'ceiling-fan', name: 'Ceiling fan', active: false, hoursOfUsage: 1, wattEnergy: 1},
        {id: 'stove', name: 'Stove', active: false, hoursOfUsage: 1, wattEnergy: 1},
        {id: 'microwave', name: 'Microwave', active: false, hoursOfUsage: 1, wattEnergy: 1},
        {id: 'dishwasher', name: 'Dishwasher', active: false, hoursOfUsage: 1, wattEnergy: 1},
        {id: 'washer', name: 'Washer', active: false, hoursOfUsage: 1, wattEnergy: 1},
        {id: 'bathtub', name: 'Bathtub', active: false, hoursOfUsage: 1, wattEnergy: 1},
        {id: 'bathtub-jets', name: 'Bath tub with spray jets', active: false, hoursOfUsage: 1, wattEnergy: 1},
        {id: 'private-pool', name: 'Private pool', active: false, hoursOfUsage: 1, wattEnergy: 1},
        {id: 'iron', name: 'Iron', active: false, hoursOfUsage: 1, wattEnergy: 1},
        {id: 'fireplace', name: 'Fireplace', active: false, hoursOfUsage: 1, wattEnergy: 1},
        {id: 'air-filtration', name: 'Air filtration', active: false, hoursOfUsage: 1, wattEnergy: 1},
        {
            id: 'high-speed-internet',
            name: 'High speed internet connection',
            active: false,
            hoursOfUsage: 1,
            wattEnergy: 1,
        },
        {id: 'wireless-internet', name: 'Wireless internet connection', active: false, hoursOfUsage: 1, wattEnergy: 1},
        {id: 'internet', name: 'Internet', active: false, hoursOfUsage: 1, wattEnergy: 1},
        {id: 'fax-machine', name: 'Fax machine', active: false, hoursOfUsage: 1, wattEnergy: 1},
        {id: 'telephone', name: 'Telephone', active: false, hoursOfUsage: 1, wattEnergy: 1},
    ])

    const app = useNuxtApp()
    const {t} = useI18n()

    watch(useHotelInfo, async () => {
        await loadData()
    })

    onMounted(async () => {
        EventBus.$on(CHANGE_LOCALE_EVENT, reload)
        if (supplierStore.fullContract?.hotelContentId || isHotelDescriptionMode) await loadData()
    })

    onBeforeUnmount(async () => {
        EventBus.$off(CHANGE_LOCALE_EVENT, reload)
    })

    async function loadData(): Promise<void> {
        const hotelId = isHotelDescriptionMode ? hotelDescriptionId : supplierStore.fullContract.hotelContentId
        try {
            isLoading.value = true
            const res = await app.$api.hotelDescriptionAmenities.get(hotelId)
            if (res.amenities?.length) {
                selectedAmenities.value = res.amenities?.filter(({active}) => active)
            }
            amenities.value = useUpdateActiveStatus.value
        } catch (e) {
            app.$toast.error(t('error_message.load_error'))
        } finally {
            isLoading.value = false
        }
    }

    const toggleAmenity = (amenity: any) => {
        if (!isHotelDescriptionMode) amenity.active = !amenity.active
    }

    const updateEnergyInputs = (amenity: any) => {
        // This function is called when an eco amenity or energy-saving solution is toggled
        // We don't need to do anything special here since the v-model binding will handle the state
        // But we could add additional logic if needed in the future
    }

    const useUpdateActiveStatus = computed(() => {
        const selectedServicesIds = selectedAmenities.value.map(({id}) => id)
        const updatedObject = clone(supplierStore.hotelServiceDictionaryInfo)
        for (const key in updatedObject) {
            if (Array.isArray(updatedObject[key])) {
                updatedObject[key].forEach(item => {
                    if (selectedServicesIds?.includes(item.id)) {
                        item.active = true
                    }
                })
            }
        }

        // ZSH Override: Filter Health care services to only show Spa and Spa procedures
        if (updatedObject.HEALTH_CARE_SERVICES && Array.isArray(updatedObject.HEALTH_CARE_SERVICES)) {
            updatedObject.HEALTH_CARE_SERVICES = updatedObject.HEALTH_CARE_SERVICES.filter(service => {
                const serviceName = service.name?.toLowerCase() || ''
                // Keep only services that are exactly "spa" or contain "spa" with "procedure"
                return serviceName === 'spa' || (serviceName.includes('spa') && serviceName.includes('procedure'))
            })
        }

        return updatedObject
    })

    async function reload(): Promise<void> {
        await supplierStore.loadHotelServiceDictionaryInfo()
        amenities.value = useUpdateActiveStatus.value
    }

    const useIsAmenitiesLoading = computed(() => {
        return supplierStore.isAmenitiesLoading
    })

    const prepareAmenities = () => {
        const resultArray = []
        // Process standard amenities
        for (const key in amenities.value) {
            if (Array.isArray(amenities.value[key])) {
                amenities.value[key].forEach(item => {
                    if (item.id && item.active !== undefined) {
                        resultArray.push({
                            id: item.id,
                            active: item.active,
                        })
                    }
                })
            }
        }

        // Process eco amenities
        ecoAmenities.value.forEach(item => {
            if (item.active) {
                resultArray.push({
                    id: item.id,
                    active: item.active,
                    hoursOfUsage: item.hoursOfUsage,
                    wattEnergy: item.wattEnergy,
                })
            }
        })

        // Process energy-saving solutions
        energySavingSolutions.value.forEach(item => {
            if (item.active) {
                resultArray.push({
                    id: item.id,
                    active: item.active,
                    hoursOfUsage: item.hoursOfUsage,
                    wattEnergy: item.wattEnergy,
                })
            }
        })

        return resultArray
    }

    const save = async (): Promise<void> => {
        const data = prepareAmenities()
        try {
            isLoading.value = true
            const hotelId = isHotelDescriptionMode ? hotelDescriptionId : supplierStore.fullContract.hotelContentId
            await app.$api.hotelDescriptionAmenities.put(hotelId, {amenities: data})
            app.$toast.success(t('success_message.saved_successfully'))
        } catch (e) {
            app.$toast.error(t('error_message.save_error'))
        } finally {
            isLoading.value = false
        }
    }
</script>
